import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Edit3, Layers, Lock, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  listAgreementTemplates,
  type AgreementTemplateSummary,
} from '@/api/agreements';

/**
 * Read-only templates landing for the Legal & Agreements module v1.
 *
 * Lists the templates seeded for this tenant (currently the Lopettia
 * Standard MoU v1) so users can see what's available when starting
 * a new agreement. The actual template editor (with rich text,
 * AI-assisted clause refinement, version diff, etc.) ships in v2.
 */

const KIND_BADGE: Record<string, string> = {
  MOU:         'bg-indigo-900/40 text-indigo-200',
  NDA:         'bg-purple-900/40 text-purple-200',
  PARTNERSHIP: 'bg-fuchsia-900/40 text-fuchsia-200',
  SOW:         'bg-teal-900/40 text-teal-200',
  DPA:         'bg-cyan-900/40 text-cyan-200',
  OTHER:       'bg-muted text-foreground',
};

export function AgreementTemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<AgreementTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAgreementTemplates()
      .then((data) => { if (!cancelled) setItems(data); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Layers className="h-6 w-6" />
            {t('agreements.templates.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('agreements.templates.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => navigate('/agreements/templates/new/edit')}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('agreements.templates.editor.newCta')}
        </Button>
      </div>

      {loading && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('common.loading')}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('agreements.templates.empty')}
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((tpl) => (
            <li key={tpl.id} className="rounded-lg border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${KIND_BADGE[tpl.kind] ?? KIND_BADGE.OTHER}`}>
                      {t(`agreements.kind.${tpl.kind}`)}
                    </span>
                    {tpl.isSystem && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground" title={t('agreements.templates.systemHint')}>
                        <Lock className="h-3 w-3" />
                        {t('agreements.templates.system')}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1 text-base font-semibold">{tpl.name}</h2>
                  {tpl.description && (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-3">{tpl.description}</p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
                <span className="text-xs text-muted-foreground">
                  {t('agreements.templates.updatedAt')}: {tpl.updatedAt ?? '—'}
                </span>
                <div className="flex items-center gap-2">
                  <Link to={`/agreements?kind=${tpl.kind}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    {t('agreements.templates.viewUsage')}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                  <Link to={`/agreements/templates/${tpl.id}/edit`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <Edit3 className="h-3 w-3" />
                    {t('agreements.action.edit')}
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* v2 callout */}
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
          <div>
            <div className="font-medium">{t('agreements.templates.editor.cta')}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t('agreements.templates.editor.bodyV3')}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/agreements/templates/new/edit')}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t('agreements.templates.editor.newCta')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
